import { type Component } from "vue";

import {
  DEACTIVATED_USER_LABEL,
  UNASSIGNED_ASSIGNEE_LABEL,
  UNASSIGNED_PSEUDO_ASSIGNEE_KEY,
  UNSET_USER_LABEL,
} from "~/components/visualization/constants";
import { DEFAULT_PROPERTY_DESCRIPTIONS } from "~/constants/property";
import { UNSET_PROPERTY_PSEUDO_VALUE } from "~/constants/pseudoDuids";
import { NUMBER_SIZE_TO_TSHIRT_SIZE } from "~/constants/size";
import {
  AssigneeFieldIcon,
  AttachmentFieldIcon,
  CheckboxFieldIcon,
  CompletedAtFieldIcon,
  CreatedAtFieldIcon,
  CreatedByFieldIcon,
  DartboardFieldIcon,
  DatesFieldIcon,
  DescriptionFieldIcon,
  LastUpdatedAtFieldIcon,
  LastUpdatedByFieldIcon,
  MultiselectFieldIcon,
  NumberFieldIcon,
  PriorityFieldIcon,
  ReviewerFieldIcon,
  SelectFieldIcon,
  SizeFieldIcon,
  StatusFieldIcon,
  TaskKindFieldIcon,
  TextFieldIcon,
  TimeTrackingFieldIcon,
  TitleFieldIcon,
  UserFieldIcon,
} from "~/icons";
import { EditorMode, Entitlement, LayoutKind, PageKind, PropertyKind } from "~/shared/enums";
import type {
  DateRange,
  Position,
  Property,
  PropertyConfig,
  PropertyDefaultKind,
  PropertyValue,
  PropertyValueForKind,
  Task,
  TimeTracking,
  TypeaheadOption,
} from "~/shared/types";
import type { useAppStore, usePageStore, useUserStore } from "~/stores";
import type { DataStore } from "~/stores/DataStore";
import { getNextTitleInSequence, getPageDisplayName } from "~/utils/common";
import {
  checkboxComparator,
  dateComparator,
  makeDateOrDatesComparator,
  makePropertyComparator,
  makeStringComparator,
  numberMaybeComparator,
  optionComparator,
  optionsComparator,
  positionComparator,
  priorityComparator,
  sizeComparator,
  statusComparator,
  taskKindComparator,
  timeTrackingComparator,
  userComparator,
  usersComparator,
} from "~/utils/comparator";
import { getOrdersBetween } from "~/utils/orderManager";
import { getRelativeTimeForDatesDate, getTimeTrackingDurationText } from "~/utils/time";
import { getUserDisplayName } from "~/utils/user";

import * as Filter from "./filter";
import * as GroupBy from "./groupBy";
import * as PropertyDisplay from "./propertyDisplay";
import * as SmartMatch from "./smartMatch";
import { checkTaskKindStatusConstraint } from "./taskConstraints";
import * as Typeahead from "./typeahead";

type AppStore = ReturnType<typeof useAppStore>;
type PageStore = ReturnType<typeof usePageStore>;
type UserStore = ReturnType<typeof useUserStore>;

const ALL_LAYOUTS = Object.values(LayoutKind);

// TODO remove all usages of this for unused types
const DummyComponent = null as unknown as Component;

let dataStore: DataStore;
let appStore: AppStore;
let pageStore: PageStore;
let userStore: UserStore;

let AdtlDates: Component;
let AdtlKind: Component;
let AdtlNumber: Component;
let AdtlSelect: Component;
let AdtlSize: Component;
let AdtlStatus: Component;
let AdtlUser: Component;
let CheckboxCellEditor: Component;
let CheckboxChip: Component;
let CheckboxEditor: Component;
let DartboardEditor: Component;
let DatesCellEditor: Component;
let DatesChip: Component;
let DatesEditor: Component;
let FormAttachmentEditor: Component;
let FormEditor: Component;
let FormNumberInput: Component;
let FormTextInput: Component;
let MultiselectChip: Component;
let NumberCellEditor: Component;
let NumberChip: Component;
let NumberEditor: Component;
let OptionCellEditor: Component;
let OptionsEditor: Component;
let PriorityCellEditor: Component;
let PriorityChip: Component;
let PriorityEditor: Component;
let SelectCellEditor: Component;
let SelectEditor: Component;
let SizeCellEditor: Component;
// TODO refactor and rename this chip, & StatusChip & UserChip
let SizeChip: Component;
let SizeEditor: Component;
let StatusCellEditor: Component;
let StatusChip: Component;
let StatusEditor: Component;
let TaskKindEditor: Component;
let TaskKindCellEditor: Component;
let TextCellEditor: Component;
let TextChip: Component;
let TextEditor: Component;
let TimeTrackingCellEditor: Component;
let TimeTrackingEditor: Component;
let UserCellEditor: Component;
let UserChip: Component;
let UserEditor: Component;
let convertMarkdownToHtml: (markdown: string) => string;
let convertToMarkdown: (text: Task["description"]) => string;

const isEmptyOrUnsetList = (value: PropertyValue) =>
  Array.isArray(value) && (value.length === 0 || (value.length === 1 && value[0] === UNSET_PROPERTY_PSEUDO_VALUE));

export const init = (
  newDataStore: DataStore,
  adtlDates: Component,
  adtlKind: Component,
  adtlNumber: Component,
  adtlSelect: Component,
  adtlSize: Component,
  adtlStatus: Component,
  adtlUser: Component,
  avatar: Component,
  checkboxCellEditor: Component,
  checkboxChip: Component,
  checkboxEditor: Component,
  dartboardEditor: Component,
  datesCellEditor: Component,
  datesChip: Component,
  datesEditor: Component,
  formAttachmentEditor: Component,
  formEditor: Component,
  multiselectChip: Component,
  numberCellEditor: Component,
  numberChip: Component,
  numberEditor: Component,
  optionCellEditor: Component,
  optionsEditor: Component,
  pageIcon: Component,
  priorityCellEditor: Component,
  priorityChip: Component,
  priorityEditor: Component,
  selectCellEditor: Component,
  selectEditor: Component,
  sizeCellEditor: Component,
  sizeEditor: Component,
  statusCellEditor: Component,
  statusChip: Component,
  statusEditor: Component,
  statusIcon: Component,
  taskKindEditor: Component,
  taskKindCellEditor: Component,
  textCellEditor: Component,
  textChip: Component,
  textEditor: Component,
  timeTrackingEditor: Component,
  timeTrackingCellEditor: Component,
  userCellEditor: Component,
  userChip: Component,
  userEditor: Component,
  formNumberInput: Component,
  formTextInput: Component,
  sizeChip: Component,
  convertMarkdownToHtmlFn: (markdown: string) => string,
  convertToMarkdownFn: (text: Task["description"]) => string
) => {
  dataStore = newDataStore;
  appStore = dataStore.$useAppStore();
  pageStore = dataStore.$usePageStore();
  userStore = dataStore.$useUserStore();

  Filter.init(dataStore, avatar, pageIcon, statusIcon);
  GroupBy.init(dataStore, avatar, pageIcon, statusIcon);
  PropertyDisplay.init(appStore);
  SmartMatch.init(dataStore);
  Typeahead.init(dataStore, avatar, pageIcon, statusIcon);

  AdtlDates = adtlDates;
  AdtlKind = adtlKind;
  AdtlNumber = adtlNumber;
  AdtlSelect = adtlSelect;
  AdtlSize = adtlSize;
  AdtlStatus = adtlStatus;
  AdtlUser = adtlUser;
  CheckboxCellEditor = checkboxCellEditor;
  CheckboxChip = checkboxChip;
  CheckboxEditor = checkboxEditor;
  DartboardEditor = dartboardEditor;
  DatesCellEditor = datesCellEditor;
  DatesChip = datesChip;
  DatesEditor = datesEditor;
  FormAttachmentEditor = formAttachmentEditor;
  FormEditor = formEditor;
  FormNumberInput = formNumberInput;
  FormTextInput = formTextInput;
  MultiselectChip = multiselectChip;
  NumberCellEditor = numberCellEditor;
  NumberChip = numberChip;
  NumberEditor = numberEditor;
  OptionCellEditor = optionCellEditor;
  OptionsEditor = optionsEditor;
  PriorityCellEditor = priorityCellEditor;
  PriorityChip = priorityChip;
  PriorityEditor = priorityEditor;
  SelectCellEditor = selectCellEditor;
  SelectEditor = selectEditor;
  SizeCellEditor = sizeCellEditor;
  SizeChip = sizeChip;
  SizeEditor = sizeEditor;
  StatusCellEditor = statusCellEditor;
  StatusChip = statusChip;
  StatusEditor = statusEditor;
  TaskKindEditor = taskKindEditor;
  TaskKindCellEditor = taskKindCellEditor;
  TextCellEditor = textCellEditor;
  TextChip = textChip;
  TextEditor = textEditor;
  TimeTrackingEditor = timeTrackingEditor;
  TimeTrackingCellEditor = timeTrackingCellEditor;
  UserCellEditor = userCellEditor;
  UserChip = userChip;
  UserEditor = userEditor;
  convertMarkdownToHtml = convertMarkdownToHtmlFn;
  convertToMarkdown = convertToMarkdownFn;
};

const getOptionsValuePretty = (duids: string[], property: Property) =>
  dataStore
    .getOptionsByDuidsOrdered(duids, property)
    .map((e) => {
      let curr = e;
      const res = [];
      for (let i = 0; i < 100; i += 1) {
        res.unshift(curr.title);
        const parent = curr.parentDuid ? dataStore.getOptionByDuid(curr.parentDuid) : null;
        if (!parent) {
          break;
        }
        curr = parent;
      }
      return res.join(" / ");
    })
    .join(", ");

const getDateValuePretty = (date: string | null) => {
  if (!date) {
    return "";
  }
  return `${date.split(".")[0]}Z`;
};

const getDatesValuePretty = (startAt: string | null, dueAt: string | null, isRange: boolean) => {
  if (!isRange) {
    if (!startAt) {
      return "";
    }
    return startAt.split("T")[0];
  }
  if (!startAt && !dueAt) {
    return "";
  }
  return `${startAt?.split("T")[0] ?? ""} -> ${dueAt?.split("T")[0] ?? ""}`;
};

const createPropertyConfig = <K extends PropertyKind>(
  kind: K,
  config: Omit<PropertyConfig<K>, "kind">
): [K, PropertyConfig<K>] => [kind, { ...config, kind }];

const AUTOMATION_KIND = createPropertyConfig(PropertyKind.DEFAULT_KIND, {
  label: "Type",
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_KIND),
  isDefault: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: TaskKindFieldIcon,
  settingsComponent: () => AdtlKind,
  getValue: (_property, task) => task.kindDuid,
  getValuePretty: (_property, task) => dataStore.getTaskKindByDuid(task.kindDuid)?.title ?? "",
  getPartialTask: (_property, _task, value) => ({ kindDuid: value }),
  typeahead: Typeahead.KIND_TYPEAHEAD,
  smartMatch: SmartMatch.KIND_SMART_MATCH,
  groupBy: GroupBy.KIND_GROUP_BY,
  filter: Filter.KIND_FILTER,
  chip: () => ({
    component: TaskKindEditor,
    getValues: (property, task) => ({
      value: task.kindDuid,
      property,
      tasks: [task],
      editorMode: EditorMode.CHIP,
    }),
  }),
  contextMenu: () => ({
    component: TaskKindEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: TaskKindEditor,
    getValues: (property, field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      hideUnset: field.hidden,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: TaskKindEditor,
    getValues: (property, task) => ({ property, tasks: [task], value: task.kindDuid }),
  }),
  automation: () => ({
    component: TaskKindEditor,
    defaultValue: () => dataStore.taskKindList[0].duid,
    getValues: (property, value, onUpdate, onAfterClose, propertyUpdates) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      value,
      hideUnset: true,
      onUpdate: (newValue: PropertyValueForKind<PropertyDefaultKind>) => {
        // If the currently selected default status is not available for the selected task kind, we need to use the default status
        const defaultStatusDuid = propertyUpdates[dataStore.defaultStatusProperty.duid] as string | undefined;
        const suggestedUpdate = checkTaskKindStatusConstraint(newValue, defaultStatusDuid, dataStore);

        if (suggestedUpdate.statusDuid === undefined) {
          onUpdate(newValue);
          return;
        }

        onUpdate(newValue, {
          ...propertyUpdates,
          [dataStore.defaultStatusProperty.duid]: suggestedUpdate.statusDuid,
        });
      },
      onAfterClose,
    }),
  }),
  listColumn: () => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: TaskKindCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) => taskKindComparator(dataStore.getTaskKindByDuid(a), dataStore.getTaskKindByDuid(b)),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => dataStore.getTaskKindByDuid(task.kindDuid)?.title ?? "",
  }),
});

const AUTOMATION_TITLE = createPropertyConfig(PropertyKind.DEFAULT_TITLE, {
  label: "Title",
  isDefault: true,
  alwaysShowForLayouts: ALL_LAYOUTS,
  lockPosition: "left",
  icon: TitleFieldIcon,
  getValue: (_property, task) => task.title,
  getValuePretty: (_property, task) => task.title,
  getPartialTask: () => ({}),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.TITLE_FILTER,
  chip: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  contextMenu: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  formField: () => ({
    component: FormTextInput,
    getValues: (property, _field, value, onUpdate) => ({
      editorMode: EditorMode.FORM,
      property,
      value,
      onUpdate,
    }),
  }),
  automation: null,
  editor: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  listColumn: () => ({}),
});

const AUTOMATION_DESCRIPTION = createPropertyConfig(PropertyKind.DEFAULT_DESCRIPTION, {
  label: "Description",
  isDefault: true,
  icon: DescriptionFieldIcon,
  getValue: (_property, task) => task.description,
  getValuePretty: (_property, task) => convertMarkdownToHtml(convertToMarkdown(task.description)),
  getPartialTask: () => ({}),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.DESCRIPTION_FILTER,
  chip: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  contextMenu: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  formField: () => ({
    component: FormEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      value,
      onUpdate,
    }),
  }),
  automation: null,
  editor: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  listColumn: () => ({}),
});

const AUTOMATION_DARTBOARD = createPropertyConfig(PropertyKind.DEFAULT_DARTBOARD, {
  label: "Dartboard",
  isDefault: true,
  icon: DartboardFieldIcon,
  getValue: (_property, task) => task.dartboardDuid,
  getValuePretty: (_property, task) =>
    getPageDisplayName(dataStore.getDartboardByDuid(task.dartboardDuid), dataStore.getSpaceByDuid),
  getPartialTask: (_property, _task, value) => ({ dartboardDuid: value }),
  typeahead: Typeahead.DARTBOARD_TYPEAHEAD,
  smartMatch: SmartMatch.DARTBOARD_SMART_MATCH,
  groupBy: GroupBy.DARTBOARD_GROUP_BY,
  filter: Filter.DARTBOARD_FILTER,
  chip: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  contextMenu: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  formField: () => ({
    component: DartboardEditor,
    getValues: (_property, field, value, onUpdate, isSubmitMode) => ({
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      hideUnset: isSubmitMode || field.hidden,
      onUpdate,
    }),
  }),
  automation: () => ({
    component: DartboardEditor,
    defaultValue: () => dataStore.defaultDartboard?.duid ?? null,
    getValues: (_property, value, onUpdate, onAfterClose) => ({
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      value,
      hideUnset: true,
      onUpdate,
      onAfterClose,
    }),
  }),
  editor: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  listColumn: () => ({}),
});

const AUTOMATION_STATUS = createPropertyConfig(PropertyKind.DEFAULT_STATUS, {
  label: PropertyKind.STATUS,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_STATUS),
  isDefault: true,
  entitlement: Entitlement.STATUSES,
  icon: StatusFieldIcon,
  settingsComponent: () => AdtlStatus,
  getValue: (_property, task) => task.statusDuid,
  getValuePretty: (_property, task) => dataStore.getStatusByDuid(task.statusDuid)?.title ?? "",
  getPartialTask: (_property, _task, value) => ({ statusDuid: value }),
  typeahead: Typeahead.STATUS_TYPEAHEAD,
  smartMatch: SmartMatch.STATUS_SMART_MATCH,
  groupBy: GroupBy.STATUS_GROUP_BY,
  filter: Filter.STATUS_FILTER,
  chip: () => ({
    component: StatusChip,
    getValues: (property, task) => ({
      value: task.statusDuid,
      property,
    }),
  }),
  contextMenu: () => ({
    component: StatusEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: StatusEditor,
    getValues: (property, field, value, onUpdate, isSubmitMode) => ({
      property,
      tasks: [],
      value,
      hideUnset: isSubmitMode || field.hidden,
      editorMode: EditorMode.FORM,
      showText: true,
      onSelect: onUpdate,
    }),
  }),
  editor: () => ({
    component: StatusEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
      showText: true,
    }),
  }),
  automation: () => ({
    component: StatusEditor,
    defaultValue: (property) => dataStore.getStatusList(property)[0].duid,
    getValues: (property, value, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      value,
      hideUnset: true,
      editorMode: EditorMode.AUTOMATION,
      showText: true,
      onSelect: onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: () => ({
    cellRenderer: StatusCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) => statusComparator(dataStore.getStatusByDuid(a), dataStore.getStatusByDuid(b)),
    suppressHeaderMenuButton: true,
    getSearchValue: (value) => {
      const status = dataStore.getStatusByDuid(value);
      if (!status) {
        const defaultStatus = dataStore.defaultDefaultUnstartedStatus;
        return `${defaultStatus.title} ${defaultStatus.kind}`;
      }
      return `${status.title} ${status.kind}`;
    },
  }),
});

const AUTOMATION_ASSIGNEES = createPropertyConfig(PropertyKind.DEFAULT_ASSIGNEES, {
  label: PropertyKind.USER,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_ASSIGNEES),
  isDefault: true,
  icon: AssigneeFieldIcon,
  settingsComponent: () => AdtlUser,
  getValue: (_property, task) => task.assigneeDuids,
  getValuePretty: (_property, task) =>
    dataStore
      .getUsersByDuidsOrdered(task.assigneeDuids)
      .map((e) => e.name || e.email)
      .join(", "),
  getPartialTask: (_property, _task, value) => {
    const valueNorm = Array.isArray(value) ? value : value ? [value] : [];
    if (valueNorm.length !== 0 && valueNorm[0] === UNASSIGNED_PSEUDO_ASSIGNEE_KEY) {
      return { assigneeDuids: [] };
    }
    return { assigneeDuids: valueNorm };
  },
  typeahead: Typeahead.USER_TYPEAHEAD,
  smartMatch: SmartMatch.USER_SMART_MATCH,
  groupBy: GroupBy.USER_GROUP_BY,
  filter: Filter.USER_FILTER,
  chip: () => ({
    component: UserChip,
    getValues: (property, task) => ({
      value: task.assigneeDuids,
      isMultiple: property.adtl.isMultiple,
      property,
    }),
  }),
  contextMenu: () => ({
    component: UserEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: UserEditor,
    getValues: (property, _field, values, onUpdate) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.FORM,
      values,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: UserEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: UserEditor,
    isValueUnset: isEmptyOrUnsetList,
    defaultValue: () => [UNSET_PROPERTY_PSEUDO_VALUE],
    getValues: (property, values, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.AUTOMATION,
      values,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    cellRenderer: UserCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) => usersComparator(dataStore.getUsersByDuidsOrdered(a), dataStore.getUsersByDuidsOrdered(b)),
    suppressHeaderMenuButton: true,
    getSearchValue: (_, task) => {
      const orderedUsers = dataStore.getUsersByDuidsOrdered(task.assigneeDuids ?? []);
      const settingsAwareUsers = property.adtl.isMultiple ? orderedUsers : orderedUsers.slice(0, 1);
      return settingsAwareUsers.length > 0
        ? settingsAwareUsers.map((e) => `${e.name} ${e.email}${e.duid === userStore.duid ? " me" : ""}`).join(" ")
        : UNASSIGNED_ASSIGNEE_LABEL;
    },
  }),
});

const AUTOMATION_DATES = createPropertyConfig(PropertyKind.DEFAULT_DATES, {
  label: "Date",
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_DATES),
  isDefault: true,
  alwaysShowForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: DatesFieldIcon,
  settingsComponent: () => AdtlDates,
  getValue: (property, task, isSortOrFilter) => {
    const { rolledUpStartAt, rolledUpDueAt } = dataStore.getTaskDates(task, isSortOrFilter && property.adtl.rollup);
    return [rolledUpStartAt, rolledUpDueAt] satisfies DateRange;
  },
  getValuePretty: (property, task) => {
    const { rolledUpStartAt, rolledUpDueAt } = dataStore.getTaskDates(task);
    return getDatesValuePretty(rolledUpStartAt, rolledUpDueAt, property.adtl.isRange);
  },
  getPartialTask: (_property, _task, value) =>
    Array.isArray(value) ? { startAt: value[0], dueAt: value[1] } : { dueAt: value },
  typeahead: Typeahead.DATES_TYPEAHEAD,
  smartMatch: SmartMatch.DATES_SMART_MATCH,
  groupBy: null,
  filter: Filter.DATES_FILTER,
  chip: () => ({
    component: DatesChip,
    getValues: (property, task) => {
      const { rolledUpStartAt, rolledUpDueAt } = dataStore.getTaskDates(task, false);
      return {
        task,
        property,
        value: [rolledUpStartAt, rolledUpDueAt],
        statusDuid: task.statusDuid,
        recurrence: task.recurrence,
        showIcon: true,
      };
    },
  }),
  contextMenu: () => ({
    component: DatesEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: DatesEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.FORM,
      value: value ?? [null, null],
      onUpdate,
    }),
  }),
  editor: () => ({
    component: DatesEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: DatesEditor,
    isValueUnset: (value) => Array.isArray(value) && value.every((e) => e === null),
    defaultValue: () => [null, null] satisfies DateRange,
    getValues: (property, value, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.AUTOMATION,
      value: value ?? [null, null],
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: DatesCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: makeDateOrDatesComparator(property.adtl.isRange),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => {
      let search = "";
      if (property.adtl.isRange && task.startAt) {
        search = `start ${getRelativeTimeForDatesDate(task.startAt)}`;
      }
      if (task.dueAt) {
        search = `${search} due ${getRelativeTimeForDatesDate(task.dueAt)}`;
      }
      return search.trim();
    },
  }),
});

const AUTOMATION_PRIORITY = createPropertyConfig(PropertyKind.DEFAULT_PRIORITY, {
  label: PropertyKind.SELECT,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_PRIORITY),
  isDefault: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: PriorityFieldIcon,
  getValue: (_property, task) => task.priority,
  getValuePretty: (_property, task) => task.priority ?? "",
  getPartialTask: (_property, _task, value) => ({ priority: value }),
  typeahead: Typeahead.PRIORITY_TYPEAHEAD,
  smartMatch: SmartMatch.PRIORITY_SMART_MATCH,
  groupBy: GroupBy.PRIORITY_GROUP_BY,
  filter: Filter.PRIORITY_FILTER,
  chip: () => ({
    component: PriorityChip,
    getValues: (property, task) => ({
      value: task.priority,
      showIcon: true,
      showBorder: true,
      property,
    }),
  }),
  contextMenu: () => ({
    component: PriorityEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: PriorityEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: PriorityEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: PriorityEditor,
    isValueUnset: (value) => value === null,
    defaultValue: () => null,
    getValues: (property, value, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      value,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: () => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: PriorityCellEditor,
    sortable: true,
    comparatorFn: (a, b) => priorityComparator(a, b),
    suppressHeaderMenuButton: true,
    getSearchValue: (value) => value ?? "(unprioritized)",
  }),
});

const AUTOMATION_TAGS = createPropertyConfig(PropertyKind.DEFAULT_TAGS, {
  label: PropertyKind.MULTISELECT,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_TAGS),
  isDefault: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: MultiselectFieldIcon,
  settingsComponent: () => AdtlSelect,
  getValue: (_property, task) => task.tagDuids,
  getValuePretty: (property, task) => getOptionsValuePretty(task.tagDuids, property),
  getPartialTask: (_property, _task, value) => ({ tagDuids: Array.isArray(value) ? value : value ? [value] : [] }),
  typeahead: Typeahead.MULTISELECT_TYPEAHEAD,
  smartMatch: SmartMatch.MULTISELECT_SMART_MATCH,
  groupBy: GroupBy.OPTION_GROUP_BY,
  filter: Filter.OPTION_FILTER,
  chip: () => ({
    component: MultiselectChip,
    getValues: (property, task) => ({
      value: task.tagDuids,
      property,
    }),
  }),
  contextMenu: () => ({
    component: OptionsEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: OptionsEditor,
    getValues: (property, _field, values, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      values,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: OptionsEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
      class: "dark-text-faintest",
    }),
  }),
  automation: () => ({
    component: OptionsEditor,
    isValueUnset: isEmptyOrUnsetList,
    defaultValue: () => [UNSET_PROPERTY_PSEUDO_VALUE],
    getValues: (property, values, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      values,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: OptionCellEditor,
    sortable: true,
    comparatorFn: (a, b) =>
      optionsComparator(
        dataStore.getOptionsByDuidsOrdered(a, property),
        dataStore.getOptionsByDuidsOrdered(b, property)
      ),
    suppressHeaderMenuButton: true,
    getSearchValue: (value) => value.map((e) => dataStore.getOptionByDuid(e)?.title).join(" "),
  }),
});

const AUTOMATION_SIZE = createPropertyConfig(PropertyKind.DEFAULT_SIZE, {
  label: PropertyKind.NUMBER,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_SIZE),
  isDefault: true,
  isNumeric: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: SizeFieldIcon,
  settingsComponent: () => AdtlSize,
  getValue: (property, task, isSortOrFilter) => dataStore.getSize(task, isSortOrFilter && property.adtl.rollup),
  getValuePretty: (_property, task) => dataStore.getSize(task)?.toString() ?? "",
  getPartialTask: (_property, _task, value) => ({ size: value }),
  typeahead: Typeahead.SIZE_TYPEAHEAD,
  smartMatch: SmartMatch.SIZE_SMART_MATCH,
  groupBy: GroupBy.SIZE_GROUP_BY,
  filter: Filter.SIZE_FILTER,
  chip: () => ({
    component: SizeChip,
    getValues: (property, task) => ({
      task,
      value: dataStore.getSize(task),
      showIcon: true,
      showBorder: true,
      property,
    }),
  }),
  contextMenu: () => ({
    component: SizeEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: SizeEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: SizeEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
      showText: true,
    }),
  }),
  automation: () => ({
    component: SizeEditor,
    isValueUnset: (value) => value === null,
    defaultValue: () => null,
    getValues: (property, value, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      value,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: () => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: SizeCellEditor,
    sortable: true,
    comparatorFn: sizeComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (value) => {
      if (!value) {
        return "(unsized)";
      }
      return `${value} ${NUMBER_SIZE_TO_TSHIRT_SIZE.get(value)}`;
    },
  }),
});

const AUTOMATION_TIME_TRACKING = createPropertyConfig(PropertyKind.DEFAULT_TIME_TRACKING, {
  label: "Time tracking",
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_TIME_TRACKING),
  isDefault: true,
  isNumeric: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: TimeTrackingFieldIcon,
  getValue: (_property, task) => task.timeTracking,
  getValuePretty: (_property, task) =>
    task.timeTracking.length > 0 ? getTimeTrackingDurationText(task.timeTracking) : "",
  getPartialTask: (_property, _task, value) => ({ timeTracking: value }),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.TIME_TRACKING_FILTER,
  chip: () => ({
    component: TimeTrackingEditor,
    getValues: (property, task) => ({
      tasks: [task],
      property,
      editorMode: EditorMode.CHIP,
    }),
  }),
  contextMenu: () => ({
    component: TimeTrackingEditor,
    getValues: (property, _task, selectedTasks) => ({
      tasks: selectedTasks,
      property,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: TimeTrackingEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: TimeTrackingEditor,
    getValues: (property, task) => ({
      tasks: [task],
      property,
    }),
  }),
  automation: null,
  listColumn: () => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: TimeTrackingCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: timeTrackingComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (value) => getTimeTrackingDurationText(value),
  }),
});

const AUTOMATION_REVIEWERS = createPropertyConfig(PropertyKind.DEFAULT_REVIEWERS, {
  label: PropertyKind.USER,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_REVIEWERS),
  isDefault: true,
  icon: ReviewerFieldIcon,
  settingsComponent: () => AdtlUser,
  getValue: (_property, task) => task.reviewerDuids,
  getValuePretty: (_property, task) =>
    dataStore
      .getUsersByDuidsOrdered(task.reviewerDuids)
      .map((e) => e.name || e.email)
      .join(", "),
  getPartialTask: (_property, _task, value) => {
    const valueNorm = Array.isArray(value) ? value : value ? [value] : [];
    if (valueNorm.length !== 0 && valueNorm[0] === UNASSIGNED_PSEUDO_ASSIGNEE_KEY) {
      return { reviewerDuids: [] };
    }
    return { reviewerDuids: valueNorm };
  },
  typeahead: Typeahead.USER_TYPEAHEAD,
  smartMatch: SmartMatch.USER_SMART_MATCH,
  groupBy: GroupBy.USER_GROUP_BY,
  filter: Filter.USER_FILTER,
  chip: () => ({
    component: UserChip,
    getValues: (property, task) => ({
      value: task.reviewerDuids,
      isMultiple: property.adtl.isMultiple,
      property,
    }),
  }),
  contextMenu: () => ({
    component: UserEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: UserEditor,
    getValues: (property, _field, values, onUpdate) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.FORM,
      values,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: UserEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: UserEditor,
    isValueUnset: isEmptyOrUnsetList,
    defaultValue: () => [UNSET_PROPERTY_PSEUDO_VALUE],
    getValues: (property, values, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.AUTOMATION,
      values,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    cellRenderer: UserCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) => usersComparator(dataStore.getUsersByDuidsOrdered(a), dataStore.getUsersByDuidsOrdered(b)),
    suppressHeaderMenuButton: true,
    getSearchValue: (_, task) => {
      const orderedUsers = dataStore.getUsersByDuidsOrdered(task.reviewerDuids ?? []);
      const settingsAwareUsers = property.adtl.isMultiple ? orderedUsers : orderedUsers.slice(0, 1);
      return settingsAwareUsers.length > 0
        ? settingsAwareUsers.map((e) => `${e.name} ${e.email}${e.duid === userStore.duid ? " me" : ""}`).join(" ")
        : UNASSIGNED_ASSIGNEE_LABEL;
    },
  }),
});

const AUTOMATION_ATTACHMENTS = createPropertyConfig(PropertyKind.DEFAULT_ATTACHMENTS, {
  label: "Attachments",
  isDefault: true,
  icon: AttachmentFieldIcon,
  getValue: (_property, task) => task.attachmentDuids,
  getValuePretty: (_property, task) => task.attachmentDuids.join(", "),
  getPartialTask: (_property, _task, value) => ({ attachmentDuids: value }),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: null,
  chip: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  contextMenu: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  formField: () => ({
    component: FormAttachmentEditor,
    getValues: (_property, _field, value, onUpdate) => ({
      attachmentDuids: value ?? [],
      onUpdate,
    }),
  }),
  editor: () => ({
    component: DummyComponent,
    getValues: () => ({}),
  }),
  automation: null,
  listColumn: () => ({}),
});

const AUTOMATION_CREATED_AT = createPropertyConfig(PropertyKind.DEFAULT_CREATED_AT, {
  label: "Date",
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_CREATED_AT),
  isDefault: true,
  readOnly: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: CreatedAtFieldIcon,
  getValue: (_property, task) => task.createdAt,
  getValuePretty: (_property, task) => getDateValuePretty(task.createdAt),
  getPartialTask: () => ({}),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.DATE_FILTER,
  chip: () => ({
    component: DatesChip,
    getValues: (property, task) => ({
      task,
      property,
      value: [null, task.createdAt],
      statusDuid: null,
      recurrence: null,
      showIcon: true,
    }),
  }),
  contextMenu: null,
  formField: () => ({
    component: DatesEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value: value ?? [null, null],
      onUpdate,
    }),
  }),
  editor: () => ({
    component: DatesEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: null,
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: DatesCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: dateComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) =>
      `${PropertyDisplay.getPropertyDisplayTitle(property).toLowerCase()} ${getRelativeTimeForDatesDate(task.createdAt)}`,
  }),
});

const AUTOMATION_CREATED_BY = createPropertyConfig(PropertyKind.DEFAULT_CREATED_BY, {
  label: PropertyKind.USER,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_CREATED_BY),
  isDefault: true,
  readOnly: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: CreatedByFieldIcon,
  settingsComponent: () => AdtlUser,
  getValue: (_property, task) => task.createdByDuid,
  getValuePretty: (_property, task) => {
    if (!task.createdByDuid) {
      return "";
    }
    const user = dataStore.getUserByDuid(task.createdByDuid);
    if (!user) {
      return "";
    }
    return getUserDisplayName(user);
  },
  getPartialTask: () => ({}),
  typeahead: null,
  smartMatch: null,
  groupBy: GroupBy.USER_GROUP_BY,
  filter: Filter.SINGLE_USER_FILTER,
  chip: () => ({
    component: UserChip,
    getValues: (property, task) => ({
      value: [task.createdByDuid],
      isMultiple: false,
      editorMode: EditorMode.BOARD,
      property,
      unsetLabel: DEACTIVATED_USER_LABEL,
    }),
  }),
  contextMenu: null,
  formField: () => ({
    component: UserEditor,
    getValues: (property, _field, values, onUpdate) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.FORM,
      values,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: UserEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: null,
  listColumn: () => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: UserCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) =>
      userComparator(a ? dataStore.getUserByDuid(a) : undefined, b ? dataStore.getUserByDuid(b) : undefined),
    suppressHeaderMenuButton: true,
    getSearchValue: (_, task) => {
      const { createdByDuid } = task;
      if (!createdByDuid) {
        return "";
      }
      const user = dataStore.getUserByDuid(createdByDuid);
      if (!user) {
        return "";
      }
      return `${user.name} ${user.duid === userStore.duid ? " me" : ""}`;
    },
  }),
});

const AUTOMATION_UPDATED_AT = createPropertyConfig(PropertyKind.DEFAULT_UPDATED_AT, {
  label: "Date",
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_UPDATED_AT),
  isDefault: true,
  readOnly: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: LastUpdatedAtFieldIcon,
  getValue: (_property, task) => task.updatedAt,
  getValuePretty: (_property, task) => getDateValuePretty(task.updatedAt),
  getPartialTask: () => ({}),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.DATE_FILTER,
  chip: () => ({
    component: DatesChip,
    getValues: (property, task) => ({
      task,
      property,
      value: [null, task.updatedAt],
      statusDuid: null,
      recurrence: null,
      showIcon: true,
    }),
  }),
  contextMenu: null,
  formField: () => ({
    component: DatesEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: DatesEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: null,
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: DatesCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: dateComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) =>
      `${PropertyDisplay.getPropertyDisplayTitle(property).toLowerCase()} ${getRelativeTimeForDatesDate(task.updatedAt)}`,
  }),
});

const AUTOMATION_UPDATED_BY = createPropertyConfig(PropertyKind.DEFAULT_UPDATED_BY, {
  label: PropertyKind.USER,
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_UPDATED_BY),
  isDefault: true,
  readOnly: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: LastUpdatedByFieldIcon,
  settingsComponent: () => AdtlUser,
  getValue: (_property, task) => task.updatedByDuid,
  getValuePretty: (_property, task) => {
    if (!task.updatedByDuid) {
      return "";
    }
    const user = dataStore.getUserByDuid(task.updatedByDuid);
    if (!user) {
      return "";
    }
    return getUserDisplayName(user);
  },
  getPartialTask: () => ({}),
  typeahead: null,
  smartMatch: null,
  groupBy: GroupBy.USER_GROUP_BY,
  filter: Filter.SINGLE_USER_FILTER,
  chip: () => ({
    component: UserChip,
    getValues: (property, task) => ({
      value: [task.updatedByDuid],
      isMultiple: false,
      editorMode: EditorMode.BOARD,
      property,
      unsetLabel: DEACTIVATED_USER_LABEL,
    }),
  }),
  contextMenu: null,
  formField: () => ({
    component: UserEditor,
    getValues: (property, _field, values, onUpdate) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.FORM,
      values,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: UserEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: null,
  listColumn: () => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: UserCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) =>
      userComparator(a ? dataStore.getUserByDuid(a) : undefined, b ? dataStore.getUserByDuid(b) : undefined),
    suppressHeaderMenuButton: true,
    getSearchValue: (_, task) => {
      const { updatedByDuid } = task;
      if (!updatedByDuid) {
        return "";
      }
      const user = dataStore.getUserByDuid(updatedByDuid);
      if (!user) {
        return "";
      }
      return `${user.name} ${user.duid === userStore.duid ? " me" : ""}`;
    },
  }),
});

const AUTOMATION_COMPLETED_AT = createPropertyConfig(PropertyKind.DEFAULT_COMPLETED_AT, {
  label: "Date",
  description: DEFAULT_PROPERTY_DESCRIPTIONS.get(PropertyKind.DEFAULT_COMPLETED_AT),
  isDefault: true,
  readOnly: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: CompletedAtFieldIcon,
  getValue: (_property, task) => task.completedAt,
  getValuePretty: (_property, task) => getDateValuePretty(task.completedAt),
  getPartialTask: () => ({}),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.NULLABLE_DATE_FILTER,
  chip: () => ({
    component: DatesChip,
    getValues: (property, task) => ({
      task,
      property,
      value: [null, task.completedAt],
      statusDuid: null,
      recurrence: null,
      showIcon: true,
    }),
  }),
  contextMenu: null,
  formField: () => ({
    component: DatesEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: DatesEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: null,
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: DatesCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: dateComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => {
      if (!task.completedAt) {
        return "";
      }
      return `${PropertyDisplay.getPropertyDisplayTitle(property).toLowerCase()} ${getRelativeTimeForDatesDate(task.completedAt)}`;
    },
  }),
});

const PROPERTY_SELECT = createPropertyConfig(PropertyKind.SELECT, {
  label: PropertyKind.SELECT,
  isDefault: false,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: SelectFieldIcon,
  settingsComponent: () => AdtlSelect,
  getValue: (property, task) => (task.properties[property.duid] as string | undefined) ?? null,
  getValuePretty: (property, task) => {
    const optionDuid = task.properties[property.duid] as string | undefined;
    return getOptionsValuePretty(optionDuid ? [optionDuid] : [], property);
  },
  getPartialTask: (property, task, value) => ({ properties: { ...task.properties, [property.duid]: value } }),
  typeahead: Typeahead.SELECT_TYPEAHEAD,
  smartMatch: SmartMatch.SELECT_SMART_MATCH,
  groupBy: GroupBy.OPTION_GROUP_BY,
  filter: Filter.OPTION_FILTER,
  chip: () => ({
    component: MultiselectChip,
    getValues: (property, task) => {
      const value = task.properties[property.duid] ?? null;
      return {
        value: value ? [value] : [],
        property,
      };
    },
  }),
  contextMenu: () => ({
    component: SelectEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: SelectEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: SelectEditor,
    getValues: (property, task) => ({ property, tasks: [task] }),
  }),
  automation: () => ({
    component: SelectEditor,
    isValueUnset: (value) => value === null || value === UNSET_PROPERTY_PSEUDO_VALUE,
    defaultValue: () => UNSET_PROPERTY_PSEUDO_VALUE,
    getValues: (property, value, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      value,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: SelectCellEditor,
    sortable: true,
    comparatorFn: (a, b) =>
      optionComparator(a ? dataStore.getOptionByDuid(a) : undefined, b ? dataStore.getOptionByDuid(b) : undefined),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => {
      const value = task.properties[property.duid] as string | undefined;
      if (!value) {
        return "";
      }
      return dataStore.getOptionByDuid(value)?.title ?? "";
    },
  }),
});

const PROPERTY_MULTISELECT = createPropertyConfig(PropertyKind.MULTISELECT, {
  label: PropertyKind.MULTISELECT,
  isDefault: false,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: MultiselectFieldIcon,
  settingsComponent: () => AdtlSelect,
  getValue: (property, task) => (task.properties[property.duid] as string[] | undefined) ?? [],
  getValuePretty: (property, task) =>
    getOptionsValuePretty((task.properties[property.duid] as string[] | undefined) ?? [], property),
  getPartialTask: (property, task, value) => ({
    properties: { ...task.properties, [property.duid]: Array.isArray(value) ? value : value ? [value] : [] },
  }),
  typeahead: Typeahead.MULTISELECT_TYPEAHEAD,
  smartMatch: SmartMatch.MULTISELECT_SMART_MATCH,
  groupBy: GroupBy.OPTION_GROUP_BY,
  filter: Filter.OPTION_FILTER,
  chip: () => ({
    component: MultiselectChip,
    getValues: (property, task) => ({
      value: task.properties[property.duid] ?? [],
      property,
    }),
  }),
  contextMenu: () => ({
    component: OptionsEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: OptionsEditor,
    getValues: (property, _field, values, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      values,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: OptionsEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: OptionsEditor,
    isValueUnset: isEmptyOrUnsetList,
    defaultValue: () => [UNSET_PROPERTY_PSEUDO_VALUE],
    getValues: (property, values, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      values,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: OptionCellEditor,
    sortable: true,
    comparatorFn: (a, b) =>
      optionsComparator(
        dataStore.getOptionsByDuidsOrdered(a, property),
        dataStore.getOptionsByDuidsOrdered(b, property)
      ),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) =>
      ((task.properties[property.duid] as string[] | undefined) ?? [])
        .map((e) => dataStore.getOptionByDuid(e)?.title)
        .join(" "),
  }),
});

const PROPERTY_STATUS = createPropertyConfig(PropertyKind.STATUS, {
  label: PropertyKind.STATUS,
  isDefault: false,
  entitlement: Entitlement.STATUSES,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: StatusFieldIcon,
  settingsComponent: () => AdtlStatus,
  getValue: (property, task) =>
    (task.properties[property.duid] as string | undefined) ?? dataStore.defaultUnstartedStatus(property).duid,
  getValuePretty: (property, task) => {
    const statusDuid = task.properties[property.duid] as string | undefined;
    if (!statusDuid) {
      return dataStore.defaultUnstartedStatus(property).title;
    }
    const status = dataStore.getStatusByDuid(statusDuid);
    if (!status) {
      return dataStore.defaultUnstartedStatus(property).title;
    }
    return status.title;
  },
  getPartialTask: (property, task, value) => ({ properties: { ...task.properties, [property.duid]: value } }),
  typeahead: Typeahead.STATUS_TYPEAHEAD,
  smartMatch: SmartMatch.STATUS_SMART_MATCH,
  groupBy: GroupBy.STATUS_GROUP_BY,
  filter: Filter.STATUS_FILTER,
  chip: () => ({
    component: StatusChip,
    getValues: (property, task) => ({
      value: task.properties[property.duid] ?? dataStore.defaultUnstartedStatus(property).duid,
      property,
    }),
  }),
  contextMenu: () => ({
    component: StatusEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: StatusEditor,
    getValues: (property, _field, value, onUpdate, isSubmitMode) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      showText: true,
      value,
      hideUnset: isSubmitMode,
      onSelect: onUpdate,
    }),
  }),
  editor: () => ({
    component: StatusEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
      showText: true,
    }),
  }),
  automation: () => ({
    component: StatusEditor,
    defaultValue: (property) => dataStore.getStatusList(property)[0].duid,
    getValues: (property, value, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      value,
      hideUnset: true,
      editorMode: EditorMode.AUTOMATION,
      showText: true,
      onSelect: onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: StatusCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) => statusComparator(dataStore.getStatusByDuid(a), dataStore.getStatusByDuid(b)),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => {
      const defaultStatus = dataStore.defaultUnstartedStatus(property);
      const value = task.properties[property.duid] as string | undefined;
      if (!value) {
        return `${defaultStatus.title} ${defaultStatus.kind}`;
      }
      const status = dataStore.getStatusByDuid(value);
      if (!status) {
        return `${defaultStatus.title} ${defaultStatus.kind}`;
      }
      return `${status.title} ${status.kind}`;
    },
  }),
});

const PROPERTY_USER = createPropertyConfig(PropertyKind.USER, {
  label: PropertyKind.USER,
  isDefault: false,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: UserFieldIcon,
  settingsComponent: () => AdtlUser,
  getValue: (property, task) => (task.properties[property.duid] as string[] | undefined) ?? [],
  getValuePretty: (property, task) =>
    dataStore
      .getUsersByDuidsOrdered((task.properties[property.duid] as string[] | undefined) ?? [])
      .map((e) => e.name || e.email)
      .join(", "),
  getPartialTask: (property, task, value) => ({
    properties: { ...task.properties, [property.duid]: Array.isArray(value) ? value : value ? [value] : [] },
  }),
  typeahead: Typeahead.USER_TYPEAHEAD,
  smartMatch: null,
  groupBy: GroupBy.USER_GROUP_BY,
  filter: Filter.USER_FILTER,
  chip: () => ({
    component: UserChip,
    getValues: (property, task) => ({
      value: task.properties[property.duid] ?? [],
      isMultiple: property.adtl.isMultiple,
      editorMode: EditorMode.BOARD,
      property,
      unsetLabel: UNSET_USER_LABEL,
    }),
  }),
  contextMenu: () => ({
    component: UserEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: UserEditor,
    getValues: (property, _field, values, onUpdate) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.FORM,
      values,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: UserEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: UserEditor,
    isValueUnset: isEmptyOrUnsetList,
    defaultValue: () => [UNSET_PROPERTY_PSEUDO_VALUE],
    getValues: (property, values, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      showText: true,
      editorMode: EditorMode.AUTOMATION,
      values,
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: UserCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: (a, b) => usersComparator(dataStore.getUsersByDuidsOrdered(a), dataStore.getUsersByDuidsOrdered(b)),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => {
      const orderedUsers = dataStore.getUsersByDuidsOrdered((task.properties[property.duid] as string[]) ?? []);
      const settingsAwareUsers = property.adtl.isMultiple ? orderedUsers : orderedUsers.slice(0, 1);
      return settingsAwareUsers.length > 0
        ? settingsAwareUsers.map((e) => `${e.name} ${e.email}${e.duid === userStore.duid ? " me" : ""}`).join(" ")
        : DEACTIVATED_USER_LABEL;
    },
  }),
});

const PROPERTY_DATES = createPropertyConfig(PropertyKind.DATES, {
  label: "Date",
  isDefault: false,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: DatesFieldIcon,
  settingsComponent: () => AdtlDates,
  getValue: (property, task, isSortOrFilter) => {
    const { rolledUpStartAt, rolledUpDueAt } = dataStore.getTaskCustomDates(
      task,
      property.duid,
      isSortOrFilter && property.adtl.rollup
    );
    return [rolledUpStartAt, rolledUpDueAt] satisfies DateRange;
  },
  getValuePretty: (property, task) => {
    const { rolledUpStartAt, rolledUpDueAt } = dataStore.getTaskCustomDates(task, property.duid);
    return getDatesValuePretty(rolledUpStartAt, rolledUpDueAt, property.adtl.isRange);
  },
  getPartialTask: (property, task, value) => ({ properties: { ...task.properties, [property.duid]: value } }),
  typeahead: Typeahead.DATES_TYPEAHEAD,
  smartMatch: SmartMatch.DATES_SMART_MATCH,
  groupBy: null,
  filter: Filter.DATES_FILTER,
  chip: () => ({
    component: DatesChip,
    getValues: (property, task) => {
      const { rolledUpStartAt, rolledUpDueAt } = dataStore.getTaskCustomDates(task, property.duid, false);
      return {
        task,
        property,
        value: [rolledUpStartAt, rolledUpDueAt],
        statusDuid: null,
        recurrence: null,
        showIcon: true,
      };
    },
  }),
  contextMenu: () => ({
    component: DatesEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: DatesEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value: value ?? [null, null],
      onUpdate,
    }),
  }),
  editor: () => ({
    component: DatesEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: DatesEditor,
    isValueUnset: (value) => Array.isArray(value) && value.every((e) => e === null),
    defaultValue: () => [null, null] satisfies DateRange,
    getValues: (property, value, onUpdate, onAfterClose) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      value: value ?? [null, null],
      onUpdate,
      onAfterClose,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: DatesCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: makeDateOrDatesComparator(property.adtl.isRange),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => {
      const { rolledUpStartAt, rolledUpDueAt } = dataStore.getTaskCustomDates(task, property.duid, false);
      let search = "";
      if (property.adtl.isRange && rolledUpStartAt) {
        search = `start ${getRelativeTimeForDatesDate(rolledUpStartAt)}`;
      }
      if (rolledUpDueAt) {
        search = `${search} end ${getRelativeTimeForDatesDate(rolledUpDueAt)}`;
      }
      return search.trim();
    },
  }),
});

const PROPERTY_TIME_TRACKING = createPropertyConfig(PropertyKind.TIME_TRACKING, {
  label: PropertyKind.TIME_TRACKING,
  isDefault: false,
  isNumeric: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: TimeTrackingFieldIcon,
  getValue: (property, task) => (task.properties[property.duid] as TimeTracking | undefined) ?? [],
  getValuePretty: (property, task) => {
    const val = (task.properties[property.duid] as TimeTracking | undefined) ?? [];
    return val.length > 0 ? getTimeTrackingDurationText(val) : "";
  },
  getPartialTask: (property, task, value) => ({ properties: { ...task.properties, [property.duid]: value } }),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.TIME_TRACKING_FILTER,
  chip: () => ({
    component: TimeTrackingEditor,
    getValues: (property, task) => ({
      tasks: [task],
      property,
      editorMode: EditorMode.CHIP,
    }),
  }),
  contextMenu: () => ({
    component: TimeTrackingEditor,
    getValues: (property, _task, selectedTasks) => ({
      tasks: selectedTasks,
      property,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: TimeTrackingEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: TimeTrackingEditor,
    getValues: (property, task) => ({
      tasks: [task],
      property,
    }),
  }),
  automation: null,
  listColumn: () => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: TimeTrackingCellEditor,
    cellRendererParams: {
      editorMode: EditorMode.LIST,
    },
    sortable: true,
    comparatorFn: timeTrackingComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (value) => getTimeTrackingDurationText(value),
  }),
});

const PROPERTY_TEXT = createPropertyConfig(PropertyKind.TEXT, {
  label: PropertyKind.TEXT,
  isDefault: false,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: TextFieldIcon,
  getValue: (property, task) => (task.properties[property.duid] as string | undefined) ?? "",
  getValuePretty: (property, task) => (task.properties[property.duid] as string | undefined) ?? "",
  getPartialTask: (property, task, value) => ({ properties: { ...task.properties, [property.duid]: value } }),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.TEXT_FILTER,
  chip: () => ({
    component: TextChip,
    getValues: (property, task) => ({
      value: task.properties[property.duid] ?? "",
      property,
    }),
  }),
  contextMenu: () => ({
    component: TextEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: FormTextInput,
    getValues: (property, _field, value, onUpdate) => ({
      editorMode: EditorMode.FORM,
      property,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: TextEditor,
    getValues: (property, task) => ({ property, tasks: [task] }),
  }),
  automation: () => ({
    component: FormTextInput,
    isValueUnset: (value) => value === "",
    defaultValue: () => "",
    getValues: (property, value, onUpdate) => ({
      editorMode: EditorMode.AUTOMATION,
      property,
      value,
      onUpdate,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: TextCellEditor,
    sortable: true,
    comparatorFn: makeStringComparator((e) => e.toLowerCase()),
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => (task.properties[property.duid] as string | undefined) ?? "",
  }),
});

const PROPERTY_NUMBER = createPropertyConfig(PropertyKind.NUMBER, {
  label: PropertyKind.NUMBER,
  isDefault: false,
  isNumeric: true,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: NumberFieldIcon,
  settingsComponent: () => AdtlNumber,
  getValue: (property, task) => (task.properties[property.duid] as number | undefined) ?? null,
  getValuePretty: (property, task) => (task.properties[property.duid] as number | undefined)?.toString() ?? "",
  getPartialTask: (property, task, value) => ({ properties: { ...task.properties, [property.duid]: value } }),
  typeahead: null,
  smartMatch: null,
  groupBy: null,
  filter: Filter.NUMBER_FILTER,
  chip: () => ({
    component: NumberChip,
    getValues: (property, task) => ({
      task,
      value: task.properties[property.duid] ?? null,
      format: property.adtl.format,
      property,
    }),
  }),
  contextMenu: () => ({
    component: NumberEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: FormNumberInput,
    getValues: (property, _field, value, onUpdate) => ({
      editorMode: EditorMode.FORM,
      property,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: NumberEditor,
    getValues: (property, task) => ({ property, tasks: [task] }),
  }),
  automation: () => ({
    component: FormNumberInput,
    isValueUnset: (value) => value === null,
    defaultValue: () => null,
    getValues: (property, value, onUpdate) => ({
      editorMode: EditorMode.AUTOMATION,
      property,
      value,
      onUpdate,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: NumberCellEditor,
    sortable: true,
    comparatorFn: numberMaybeComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => (task.properties[property.duid] as number | undefined)?.toString() ?? "",
  }),
});

const PROPERTY_CHECKBOX = createPropertyConfig(PropertyKind.CHECKBOX, {
  label: PropertyKind.CHECKBOX,
  isDefault: false,
  defaultHideForLayouts: [LayoutKind.CALENDAR, LayoutKind.ROADMAP],
  icon: CheckboxFieldIcon,
  getValue: (property, task) => (task.properties[property.duid] as boolean | undefined) ?? false,
  getValuePretty: (property, task) => (((task.properties[property.duid] as boolean | undefined) ?? false) ? "X" : ""),
  getPartialTask: (property, task, value) => ({ properties: { ...task.properties, [property.duid]: value } }),
  typeahead: Typeahead.CHECKBOX_TYPEAHEAD,
  smartMatch: SmartMatch.CHECKBOX_SMART_MATCH,
  groupBy: GroupBy.CHECKBOX_GROUP_BY,
  filter: Filter.CHECKBOX_FILTER,
  chip: () => ({
    component: CheckboxChip,
    getValues: (property, task) => ({
      value: task.properties[property.duid] ?? false,
      property,
    }),
  }),
  contextMenu: () => ({
    component: CheckboxEditor,
    getValues: (property, _task, selectedTasks) => ({
      property,
      tasks: selectedTasks,
      editorMode: EditorMode.CONTEXT_MENU,
    }),
  }),
  formField: () => ({
    component: CheckboxEditor,
    getValues: (property, _field, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.FORM,
      value,
      onUpdate,
    }),
  }),
  editor: () => ({
    component: CheckboxEditor,
    getValues: (property, task) => ({
      property,
      tasks: [task],
    }),
  }),
  automation: () => ({
    component: CheckboxEditor,
    defaultValue: () => false,
    getValues: (property, value, onUpdate) => ({
      property,
      tasks: [],
      editorMode: EditorMode.AUTOMATION,
      value,
      onUpdate,
    }),
  }),
  listColumn: (property) => ({
    hide: pageStore.isScreenExtraSmallOrNarrower,
    cellRenderer: CheckboxCellEditor,
    sortable: true,
    comparatorFn: checkboxComparator,
    suppressHeaderMenuButton: true,
    getSearchValue: (_value, task) => {
      const value = (task.properties[property.duid] as boolean | undefined) ?? false;
      return value ? "checked" : "unchecked";
    },
  }),
});

// All things property-related
const PROPERTY_KIND_TO_CONFIG_MAP: { [K in PropertyKind]: PropertyConfig<K> } = Object.fromEntries([
  AUTOMATION_KIND,
  AUTOMATION_TITLE,
  AUTOMATION_DESCRIPTION,
  AUTOMATION_DARTBOARD,
  AUTOMATION_STATUS,
  AUTOMATION_ASSIGNEES,
  AUTOMATION_DATES,
  AUTOMATION_PRIORITY,
  AUTOMATION_TAGS,
  AUTOMATION_SIZE,
  AUTOMATION_TIME_TRACKING,
  AUTOMATION_REVIEWERS,
  AUTOMATION_ATTACHMENTS,
  AUTOMATION_CREATED_AT,
  AUTOMATION_CREATED_BY,
  AUTOMATION_UPDATED_AT,
  AUTOMATION_UPDATED_BY,
  AUTOMATION_COMPLETED_AT,
  PROPERTY_SELECT,
  PROPERTY_MULTISELECT,
  PROPERTY_STATUS,
  PROPERTY_USER,
  PROPERTY_DATES,
  PROPERTY_TIME_TRACKING,
  PROPERTY_TEXT,
  PROPERTY_NUMBER,
  PROPERTY_CHECKBOX,
]);

// TODO move these helper functions out of this file
export const getProperty = (propertyDuid: string): Property | undefined => dataStore.getPropertyByDuid(propertyDuid);

export const getPropertyConfig = <K extends PropertyKind>(propertyKind: K): PropertyConfig<K> =>
  PROPERTY_KIND_TO_CONFIG_MAP[propertyKind];

export const getPropertyWithConfig = (propertyDuid: string): [Property, PropertyConfig] | undefined => {
  const property = dataStore.getPropertyByDuid(propertyDuid);
  if (!property) {
    return undefined;
  }
  return [property, getPropertyConfig(property.kind)];
};

export const getPropertyWithConfigList = (): [Property, PropertyConfig][] =>
  dataStore.propertyList.map((e) => [e, getPropertyConfig(e.kind)] satisfies [Property, PropertyConfig]);

export { getPropertyDisplayTitle } from "./propertyDisplay";

export const getShownPropertyWithConfigList = (
  alwaysIncludeKinds: PropertyKind[] = [],
  alwaysIncludeDuids: string[] = []
): [Property, PropertyConfig][] => {
  const fullComparator = makePropertyComparator(appStore.propertyOrderDuids);
  const comparator = (a: Property, aPosition: Position | undefined, b: Property, bPosition: Position | undefined) =>
    positionComparator(aPosition, bPosition) || fullComparator(a.duid, b.duid);
  return getPropertyWithConfigList()
    .filter(([property, config]) => {
      if (alwaysIncludeKinds.includes(property.kind) || alwaysIncludeDuids.includes(property.duid)) {
        return true;
      }
      return appStore.isPropertyShownInLayout(property, config);
    })
    .sort((a, b) => comparator(a[0], a[1].lockPosition, b[0], b[1].lockPosition));
};

export const showPropertiesWithConfig = (cachedShownPropertyDuids: string[]) => {
  // TODO this has the wrong order for properties that are not shown (and therefore shown in the dropdown)
  // they should be in 'standard' order per the properties settings page, but they are ordered by appStore.propertyOrderDuids, which could have a cached value for them
  const fullComparator = makePropertyComparator(appStore.propertyOrderDuids);
  const comparator = (aDuid: string, aPosition: Position | undefined, bDuid: string, bPosition: Position | undefined) =>
    positionComparator(aPosition, bPosition) || fullComparator(aDuid, bDuid);
  const alwaysIncludeKinds = [PropertyKind.DEFAULT_TITLE];
  if (appStore.currentPage?.pageKind === PageKind.VIEW) {
    alwaysIncludeKinds.push(PropertyKind.DEFAULT_DARTBOARD);
  }
  return getShownPropertyWithConfigList(alwaysIncludeKinds, cachedShownPropertyDuids)
    .map(([property, config]): { property: Property; config: PropertyConfig; hidden: boolean; locked: boolean } => {
      const locked =
        config.alwaysShowForLayouts?.includes(appStore.layoutKind) ??
        alwaysIncludeKinds.includes(property.kind) ??
        false;
      return {
        property,
        config,
        hidden: !locked && !appStore.isPropertyShownInLayout(property, config),
        locked,
      };
    })
    .sort((a, b) => comparator(a.property.duid, a.config.lockPosition, b.property.duid, b.config.lockPosition));
};

export const getPropertyValueFromTaskByDuid = (propertyDuid: string, task: Task, isSorting: boolean = false) => {
  const propertyAndConfig = getPropertyWithConfig(propertyDuid);
  if (!propertyAndConfig) {
    return undefined;
  }
  const [property, propertyConfig] = propertyAndConfig;
  return propertyConfig.getValue(property, task, isSorting);
};

export const getPropertyValueFromTask = <P extends Property>(property: P, task: Task, isSorting: boolean = false) => {
  type Kind = P["kind"];
  const propertyConfig = getPropertyConfig<Kind>(property.kind);
  return propertyConfig.getValue(property, task, isSorting);
};

export const getPropertyPartialTask = <P extends Property>(property: P, task: Partial<Task>, value: PropertyValue) => {
  const propertyConfig = getPropertyConfig(property.kind);
  return propertyConfig.getPartialTask(property, task, value);
};

// TODO remove this
export const getDefaultPropertiesToFieldsMap = () =>
  new Map<Property, keyof Task>([
    [dataStore.defaultKindProperty, "kindDuid"],
    [dataStore.defaultStatusProperty, "statusDuid"],
    [dataStore.defaultAssigneesProperty, "assigneeDuids"],
    [dataStore.defaultDatesProperty, "dueAt"],
    [dataStore.defaultPriorityProperty, "priority"],
    [dataStore.defaultTagsProperty, "tagDuids"],
    [dataStore.defaultSizeProperty, "size"],
    [dataStore.defaultTimeTrackingProperty, "timeTracking"],
    [dataStore.defaultReviewersProperty, "reviewerDuids"],
  ]);

export const getPropertyTypeaheadOption = async (
  propertyDuid: string,
  value: TypeaheadOption["value"]
): Promise<[Property, TypeaheadOption | null] | null> => {
  const propertyWithConfig = getPropertyWithConfig(propertyDuid);
  if (!propertyWithConfig) {
    return null;
  }

  const [property, config] = propertyWithConfig;
  if (!config.typeahead) {
    return null;
  }

  // TODO really need to change this paradigm for typeaheads. should be able to construct an option from a value and not have to search through everything to find it. this will also allow us to remove the string part from all of the query: string weirdness
  const typeaheadOption = (
    await Promise.all(
      config
        .typeahead(property)
        .flatMap((typeahead) => typeahead.getOptions)
        .map((getOptions) => getOptions(value.toString()))
        .flat()
    )
  )
    .flatMap((e) => e.options)
    .find((option) => option.value === value);

  return [property, typeaheadOption ?? null];
};

export const getPropertyValueStr = (value: PropertyValue): string =>
  value === null || (Array.isArray(value) && value.length === 0) ? GroupBy.NO_VALUE_GROUP_BY_KEY : value.toString();

export const createCustomProperty = async (kind: PropertyKind, title?: string): Promise<Property | undefined> => {
  const comparator = makePropertyComparator([]);
  const allProperties = dataStore.propertyList
    .map((p) => ({ ...p, config: getPropertyConfig(p.kind) }))
    .sort((a, b) => (a.hidden || b.hidden ? 1 : comparator(a.duid, b.duid)));
  const customProperties = allProperties.filter((property) => !property.config.isDefault);

  const indexOfLastDefaultProperty = allProperties.map((e) => e.config.isDefault).lastIndexOf(true);
  const bottomDefaultPropertyOrder = allProperties[indexOfLastDefaultProperty].order;
  const firstCustomPropertyOrder = allProperties[indexOfLastDefaultProperty + 1]?.order;
  const order = getOrdersBetween(bottomDefaultPropertyOrder, firstCustomPropertyOrder)[0];

  const config = getPropertyConfig(kind);
  const newProperty = await dataStore.createProperty(kind, order, {
    title: getNextTitleInSequence(
      title || config.label,
      customProperties.map((e) => e.title),
      { noCopy: true }
    ),
  });

  return newProperty;
};
